
import Vue, { VueConstructor } from 'vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import multiMonthPlugin from '@fullcalendar/multimonth'
import frLocale from '@fullcalendar/core/locales/fr'
import enLocale from '@fullcalendar/core/locales/en-gb'
import axios from 'axios'
import { EventBus } from '@/plugins/eventBus'
import { getUserId } from '@/plugins/token'
import { state } from '@/plugins/state'
import dateFormatMixin from '@/mixins/DateFormatMixin.vue'
import EventFormDialog from '@/components/calendar/EventFormDialog.vue'
import DateTimePicker2 from '@/components/DateTimePicker2.vue'
import {
  CalendarEvent,
  CalendarEventFilters,
  EventCategory,
  getCategoryColors,
  getEventColor,
} from '@/types/Calendar'

type QueryParamValue = string | string[] | number | undefined

function getUserDisplayName(isCurrentUser: boolean, fullName: string, meLabel: string): string {
  if (!isCurrentUser) return fullName
  if (fullName && fullName.trim() !== meLabel) return `${meLabel} (${fullName})`
  return meLabel
}

interface DataType {
  currentView: 'calendar' | 'list'
  calendarViewType: string
  calendarTitle: string
  loading: boolean
  events: CalendarEvent[]
  totalEvents: number
  filters: CalendarEventFilters
  eventDialog: boolean
  deleteDialog: boolean
  deleting: boolean
  selectedEvent: CalendarEvent | null
  preselectedFromQuery: {
    type: string
    title: string
    companyIds: number[]
    workerIds: number[]
    userIds: number[]
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    description: string
    address: string
    result: string
    isPrivate: boolean
  }
  users: Array<{ id: number; first_name: string; last_name: string; full_name: string }>
  spaces: Array<{ id: number; name: string }>
  defaultUserFilterSet: boolean
  tableOptions: { page: number; itemsPerPage: number; sortBy: string[]; sortDesc: boolean[] }
  calendarOptions: any
  googleCalendarConnected: boolean
  microsoftCalendarConnected: boolean
  categoryColorsVersion: number
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof dateFormatMixin>>).extend({
  name: 'Calendar',
  components: {
    FullCalendar,
    EventFormDialog,
    DateTimePicker2,
  },
  mixins: [dateFormatMixin],
  data(): DataType {
    return {
      currentView: 'calendar',
      calendarViewType: 'dayGridMonth',
      calendarTitle: '',
      loading: false,
      events: [],
      totalEvents: 0,
      filters: {
        space_ids: state.agency ? [Number(state.agency)] : [] as number[],
        user_ids: [] as number[],
        categories: [],
        types: [],
        from: undefined,
        to: undefined,
        company_id: undefined,
        worker_id: undefined,
      },
      eventDialog: false,
      deleteDialog: false,
      deleting: false,
      selectedEvent: null,
      preselectedFromQuery: {
        type: '',
        title: '',
        companyIds: [] as number[],
        workerIds: [] as number[],
        userIds: [] as number[],
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        description: '',
        address: '',
        result: '',
        isPrivate: false,
      },
      users: [],
      spaces: [],
      defaultUserFilterSet: false,
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['start_date'],
        sortDesc: [true],
      },
      googleCalendarConnected: false,
      microsoftCalendarConnected: false,
      categoryColorsVersion: 0,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin],
        initialView: 'dayGridMonth',
        locales: [frLocale, enLocale],
        locale: 'fr',
        headerToolbar: false,
        height: 'auto',
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        allDaySlot: false,
        weekends: true,
        nowIndicator: true,
        eventDisplay: 'block',
        views: {
          multiMonthYear: {
            type: 'multiMonth',
            duration: { months: 12 },
            multiMonthMaxColumns: 3,
            dateIncrement: { years: 1 },
            visibleRange: (currentDate: Date) => {
              const year = currentDate.getFullYear()
              return {
                start: new Date(year, 0, 1).toISOString().slice(0, 10),
                end: new Date(year + 1, 0, 1).toISOString().slice(0, 10),
              }
            },
          },
        },
      } as any,
    }
  },
  computed: {
    listHeaders() {
      return [
        { text: this.$vuetify.lang.t('$vuetify.type'), value: 'type', sortable: true },
        { text: this.$vuetify.lang.t('$vuetify.title'), value: 'title', sortable: true },
        { text: this.$vuetify.lang.t('$vuetify.description'), value: 'description', sortable: true },
        { text: this.$vuetify.lang.t('$vuetify.result'), value: 'result', sortable: true },
        { text: this.$vuetify.lang.t('$vuetify.start_date'), value: 'start_date', sortable: true },
        { text: '', value: 'is_private', sortable: false, width: 40 },
        { text: this.$vuetify.lang.t('$vuetify.action'), value: 'actions', sortable: false, width: 120 },
      ]
    },
    selectedUsersInListCount(): number {
      const userIds = new Set(this.users.map((u: { id: number }) => u.id))
      return (this.filters.user_ids || []).filter((id: number) => userIds.has(id)).length
    },
    allUsersSelected(): boolean {
      return this.users.length > 0 &&
        this.selectedUsersInListCount === this.users.length
    },
    usersSelectionIndeterminate(): boolean {
      const count = this.selectedUsersInListCount
      return count > 0 && count < this.users.length
    },
    categoryItems() {
      const version = this.categoryColorsVersion
      const colors = getCategoryColors()
      return [
        { text: this.$vuetify.lang.t('$vuetify.event_category.quality'), value: 'quality', color: colors.quality },
        { text: this.$vuetify.lang.t('$vuetify.event_category.commercial'), value: 'commercial', color: colors.commercial },
        { text: this.$vuetify.lang.t('$vuetify.event_category.recruitment'), value: 'recruitment', color: colors.recruitment },
        { text: this.$vuetify.lang.t('$vuetify.event_category.internal'), value: 'internal', color: colors.internal },
        { text: this.$vuetify.lang.t('$vuetify.event_category.follow_up'), value: 'follow_up', color: colors.follow_up },
        { text: this.$vuetify.lang.t('$vuetify.event_category.other'), value: 'other', color: colors.other },
      ].map((c) => {
        const { _v, ...rest } = { ...c, _v: version }
        return rest
      })
    },
    hasActiveFilters(): boolean {
      return !!(
        (this.filters.space_ids && this.filters.space_ids.length > 0) ||
        (this.filters.user_ids && this.filters.user_ids.length > 0) ||
        (this.filters.categories && this.filters.categories.length > 0) ||
        this.filters.from ||
        this.filters.to
      )
    },
    activeFilterChips() {
      const chips: Array<{ key: string; label: string }> = []
      if (this.filters.space_ids && this.filters.space_ids.length > 0) {
        this.filters.space_ids.forEach((sid: number) => {
          const space = this.spaces.find(s => s.id === sid)
          if (space) chips.push({ key: `space_id_${sid}`, label: space.name })
        })
      }
      if (this.filters.user_ids && this.filters.user_ids.length > 0) {
        this.filters.user_ids.forEach((uid: number) => {
          const user = this.users.find(u => u.id === uid)
          if (user) chips.push({ key: `user_id_${uid}`, label: user.full_name })
        })
      }
      if (this.filters.categories && this.filters.categories.length > 0) {
        this.filters.categories.forEach((cat: EventCategory) => {
          chips.push({ key: `category_${cat}`, label: this.$vuetify.lang.t(`$vuetify.event_category.${cat}`) as string })
        })
      }
      if (this.filters.from) {
        chips.push({ key: 'from', label: `${this.$vuetify.lang.t('$vuetify.from')}: ${this.filters.from}` })
      }
      if (this.filters.to) {
        chips.push({ key: 'to', label: `${this.$vuetify.lang.t('$vuetify.to')}: ${this.filters.to}` })
      }
      return chips
    },
    previousLabel(): string {
      switch (this.calendarViewType) {
        case 'timeGridWeek':
          return (this.$vuetify.lang.t('$vuetify.previous_week') as string).toUpperCase()
        case 'timeGridDay':
          return (this.$vuetify.lang.t('$vuetify.previous_day') as string).toUpperCase()
        case 'multiMonthYear':
          return (this.$vuetify.lang.t('$vuetify.previous_year') as string).toUpperCase()
        default:
          return (this.$vuetify.lang.t('$vuetify.previous_month') as string).toUpperCase()
      }
    },
    nextLabel(): string {
      switch (this.calendarViewType) {
        case 'timeGridWeek':
          return (this.$vuetify.lang.t('$vuetify.next_week') as string).toUpperCase()
        case 'timeGridDay':
          return (this.$vuetify.lang.t('$vuetify.next_day') as string).toUpperCase()
        case 'multiMonthYear':
          return (this.$vuetify.lang.t('$vuetify.next_year') as string).toUpperCase()
        default:
          return (this.$vuetify.lang.t('$vuetify.next_month') as string).toUpperCase()
      }
    },
    dateFromValue(): { dateObject: Date; dateString: string; timeString: string; endTimeString: string } | null {
      if (!this.filters.from) return null
      const [y, m, d] = this.filters.from.split('-').map(Number)
      const date = new Date(y, (m || 1) - 1, d || 1)
      return {
        dateObject: date,
        dateString: this.filters.from,
        timeString: '00:00',
        endTimeString: '00:00',
      }
    },
    dateToValue(): { dateObject: Date; dateString: string; timeString: string; endTimeString: string } | null {
      if (!this.filters.to) return null
      const [y, m, d] = this.filters.to.split('-').map(Number)
      const date = new Date(y, (m || 1) - 1, d || 1)
      return {
        dateObject: date,
        dateString: this.filters.to,
        timeString: '00:00',
        endTimeString: '00:00',
      }
    },
    dateFromPickerProps(): Record<string, unknown> {
      const base = { locale: this.$vuetify.lang.current }
      if (this.filters.to) {
        return { ...base, max: this.filters.to }
      }
      return base
    },
    dateToPickerProps(): Record<string, unknown> {
      const base = { locale: this.$vuetify.lang.current }
      if (this.filters.from) {
        return { ...base, min: this.filters.from }
      }
      return base
    },
  },
  watch: {
    calendarViewType(newView: string) {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        calendarApi.changeView(newView)
        if (newView === 'multiMonthYear') {
          const date = calendarApi.getDate()
          const jan1 = new Date(date.getFullYear(), 0, 1)
          calendarApi.gotoDate(jan1)
        }
        this.updateCalendarTitle()
      }
    },
    currentView(newView: string) {
      if (newView === 'list') {
        this.fetchEvents()
      } else if (newView === 'calendar') {
        // Reinitialize calendar when switching back to calendar view
        // The component is recreated with v-if, so we need to wait for it to mount
        this.$nextTick(() => {
          setTimeout(() => {
            this.initCalendar()
          }, 100)
        })
      }
    },
    '$route.query.create'(newVal: string | undefined) {
      // Open dialog when create parameter is added to query (even if already on calendar page)
      if (newVal === 'true') {
        this.preselectedFromQuery = this.buildPreselectedFromQuery()
        this.$nextTick(() => {
          this.selectedEvent = null
          this.eventDialog = true
          this.$router.replace({ query: {} }).catch(() => { /* NavigationDuplicated */ })
        })
      }
    },
    eventDialog(newVal: boolean) {
      if (!newVal) {
        this.preselectedFromQuery = {
          type: '',
          title: '',
          companyIds: [],
          workerIds: [],
          userIds: [],
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          description: '',
          address: '',
          result: '',
          isPrivate: false,
        }
      }
    },
  },
  async mounted() {
    this.fetchUsers()
    this.fetchAgencies()
    await Promise.all([this.fetchGoogleCalendarStatus(), this.fetchMicrosoftCalendarStatus()])
    this.initCalendar()

    // Check if we should open the dialog from query parameter
    if (this.$route.query.create === 'true') {
      this.preselectedFromQuery = this.buildPreselectedFromQuery()
      this.$nextTick(() => {
        this.selectedEvent = null
        this.eventDialog = true
        this.$router.replace({ query: {} }).catch(() => { /* NavigationDuplicated */ })
      })
    }
  },
  methods: {
    getEventColor,
    onFromDateInput(val: { dateString?: string } | { dateString?: string }[] | null) {
      const item = Array.isArray(val) ? val[0] : val
      this.filters.from = item?.dateString || undefined
      this.refreshEvents()
    },
    onToDateInput(val: { dateString?: string } | { dateString?: string }[] | null) {
      const item = Array.isArray(val) ? val[0] : val
      this.filters.to = item?.dateString || undefined
      this.refreshEvents()
    },
    initCalendar() {
      this.$nextTick(() => {
        const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
        if (calendarApi) {
          // Set options via API to ensure they're properly registered
          calendarApi.setOption('locale', this.$vuetify.lang.current)
          calendarApi.setOption('select', this.handleDateSelect.bind(this))
          calendarApi.setOption('eventClick', this.handleEventClick.bind(this))
          calendarApi.setOption('datesSet', this.handleDatesSet.bind(this))

          // Add Dispojob event source
          calendarApi.addEventSource({
            events: this.fetchCalendarEvents.bind(this)
          })

          // Add Google Calendar event source when connected
          if (this.googleCalendarConnected) {
            this.addGoogleEventSource(calendarApi)
          }
          // Add Microsoft Calendar event source when connected
          if (this.microsoftCalendarConnected) {
            this.addMicrosoftEventSource(calendarApi)
          }

          this.updateCalendarTitle()
        }
      })
    },
    updateCalendarTitle() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        if (this.calendarViewType === 'multiMonthYear' && calendarApi.view?.currentStart) {
          this.calendarTitle = String(calendarApi.view.currentStart.getFullYear())
        } else {
          this.calendarTitle = calendarApi.view?.title ?? ''
        }
      }
    },
    calendarPrev() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        calendarApi.prev()
        this.updateCalendarTitle()
      }
    },
    calendarNext() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        calendarApi.next()
        this.updateCalendarTitle()
      }
    },
    calendarToday() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        if (this.calendarViewType === 'multiMonthYear') {
          const now = new Date()
          const jan1 = new Date(now.getFullYear(), 0, 1)
          calendarApi.gotoDate(jan1)
        } else {
          calendarApi.today()
        }
        this.updateCalendarTitle()
      }
    },
    handleDatesSet() {
      this.updateCalendarTitle()
    },
    handleDateSelect(selectInfo: any) {
      this.selectedEvent = {
        id: 0,
        user_id: 0,
        type: '',
        title: '',
        description: null,
        address: null,
        address_position: null,
        result: null,
        start_date: selectInfo.startStr,
        end_date: selectInfo.endStr || this.addMinutes(selectInfo.startStr, 15),
        is_private: false,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        users: [],
        workers: [],
        companies: [],
        contacts: [],
        notification: null,
        category: 'other',
      }
      this.eventDialog = true
    },
    handleEventClick(clickInfo: any) {
      // Google/Microsoft Calendar events: open in new tab (prevent default navigation in same tab)
      if ((clickInfo.event.extendedProps?.source === 'google' || clickInfo.event.extendedProps?.source === 'microsoft') && clickInfo.event.url) {
        clickInfo.jsEvent.preventDefault()
        globalThis.open(clickInfo.event.url, '_blank')
        return
      }
      // Use extendedProps directly - it contains the full event data with correct date format
      this.selectedEvent = clickInfo.event.extendedProps as CalendarEvent
      this.eventDialog = true
    },
    fetchCalendarEvents(
      fetchInfo: any,
      successCallback: (events: any[]) => void,
      failureCallback: (error: any) => void
    ): void {
      this.loading = true
      const params: Record<string, any> = {
        from: fetchInfo.startStr.split('T')[0] + ' 00:00:00',
        to: fetchInfo.endStr.split('T')[0] + ' 23:59:59',
        per_page: 0, // Get all events for calendar view
      }

      if (this.filters.user_ids && this.filters.user_ids.length > 0) {
        params.user_ids = this.filters.user_ids.join(',')
      }
      if (this.filters.space_ids && this.filters.space_ids.length > 0) {
        params.space_ids = this.filters.space_ids.join(',')
      }
      if (this.filters.categories && this.filters.categories.length > 0) {
        params.categories = this.filters.categories.join(',')
      }

      axios.get('/v1/calendar/events', { params })
        .then((response) => {
          let events = Array.isArray(response.data) ? response.data : (response.data.data || [])
          const categories = this.filters.categories || []
          if (categories.length > 0) {
            events = events.filter((event: CalendarEvent) => categories.includes(event.category))
          }

          const calendarEvents = events.map((event: CalendarEvent) => ({
            id: String(event.id),
            title: event.title,
            start: event.start_date.replace(' ', 'T'),
            end: event.end_date.replace(' ', 'T'),
            backgroundColor: getEventColor(event),
            borderColor: getEventColor(event),
            extendedProps: event,
          }))

          successCallback(calendarEvents)
        })
        .catch((error) => {
          EventBus.$emit('snackbar', { axiosError: error })
          failureCallback(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async fetchEvents() {
      try {
        this.loading = true
        const params = this.buildFetchEventsParams()
        const response = await axios.get('/v1/calendar/events', { params })
        const data = response.data
        let dispojobEvents: CalendarEvent[] = Array.isArray(data) ? data : (data.data || [])

        const externalEvents = await this.fetchExternalCalendarEvents()
        const categories = this.filters.categories || []
        const sortBy = this.tableOptions.sortBy[0] || 'start_date'
        const sortDesc = this.tableOptions.sortDesc[0] !== false
        let merged = this.mergeAndSortEvents(dispojobEvents, externalEvents, sortBy, sortDesc)
        if (categories.length > 0) {
          merged = merged.filter((evt: CalendarEvent) => categories.includes(evt.category))
        }
        this.events = merged.map((evt: any) => ({
          ...evt,
          rowKey: evt.rowKey || String(evt.id),
        }))
        this.totalEvents = this.computeTotalEvents(data, this.events.length)
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error })
      } finally {
        this.loading = false
      }
    },
    buildFetchEventsParams(): Record<string, any> {
      const params: Record<string, any> = { per_page: 0 }
      if (this.tableOptions.sortBy.length > 0) {
        params.sort_by = this.tableOptions.sortBy[0]
        params.sort_order = this.tableOptions.sortDesc[0] ? 'desc' : 'asc'
      }
      if (this.filters.user_ids?.length) params.user_ids = this.filters.user_ids.join(',')
      if (this.filters.space_ids?.length) params.space_ids = this.filters.space_ids.join(',')
      if (this.filters.from) params.from = this.filters.from
      if (this.filters.to) params.to = this.filters.to
      if (this.filters.categories?.length) params.categories = this.filters.categories.join(',')
      return params
    },
    async fetchExternalCalendarEvents(): Promise<CalendarEvent[]> {
      const externalEvents: CalendarEvent[] = []
      const categories = this.filters.categories || []
      const includeExternal = categories.length === 0 || categories.includes('other')
      if (!includeExternal) return externalEvents

      const fromStr = this.filters.from || this.getDefaultDateRange().from
      const toStr = this.filters.to || this.getDefaultDateRange().to
      const fetchPromises: Promise<void>[] = []

      if (this.shouldShowGoogleCalendar()) {
        fetchPromises.push(
          axios.get('/v1/calendar/google/events', { params: { from: fromStr, to: toStr } })
            .then((res) => {
              const d = res.data
              if (d?.tokenInvalidated) this.googleCalendarConnected = false
              const evts = Array.isArray(d) ? d : (d?.events ?? [])
              externalEvents.push(...this.externalEventsToListFormat(evts, 'google'))
            })
            .catch((err) => { EventBus.$emit('snackbar', { axiosError: err }) })
        )
      }
      if (this.shouldShowMicrosoftCalendar()) {
        fetchPromises.push(
          axios.get('/v1/calendar/microsoft/events', { params: { from: fromStr, to: toStr } })
            .then((res) => {
              const d = res.data
              if (d?.tokenInvalidated) this.microsoftCalendarConnected = false
              const evts = Array.isArray(d) ? d : (d?.events ?? [])
              externalEvents.push(...this.externalEventsToListFormat(evts, 'microsoft'))
            })
            .catch((err) => { EventBus.$emit('snackbar', { axiosError: err }) })
        )
      }
      await Promise.all(fetchPromises)
      return externalEvents
    },
    computeTotalEvents(data: any, eventsLength: number): number {
      const paginateTotal = data.paginate?.total
      const metaTotal = data.meta?.total
      if (typeof paginateTotal === 'number') return paginateTotal
      if (typeof metaTotal === 'number') return metaTotal
      return eventsLength
    },
    getDefaultDateRange(): { from: string; to: string } {
      const now = new Date()
      const from = new Date(now.getFullYear(), 0, 1)
      const to = new Date(now.getFullYear() + 1, 11, 31)
      return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      }
    },
    externalEventsToListFormat(events: any[], source: 'google' | 'microsoft'): CalendarEvent[] {
      const currentUserId = getUserId()
      const userId = currentUserId ? Number(currentUserId) : 0
      return events.map((evt: any, index: number) => {
        const start = evt.start || ''
        const end = evt.end || start
        const startDate = start.includes('T') ? start.replace('T', ' ').slice(0, 19) : start + ' 00:00:00'
        const endDate = end.includes('T') ? end.replace('T', ' ').slice(0, 19) : end + ' 23:59:59'
        const rowKey = evt.id || `${source}-${index}-${startDate}-${evt.title || ''}`
        return {
          id: 0,
          rowKey,
          user_id: userId,
          type: 'autre',
          title: evt.title || '(Sans titre)',
          description: null,
          address: null,
          address_position: null,
          result: null,
          start_date: startDate,
          end_date: endDate,
          is_private: false,
          created_at: '',
          updated_at: '',
          deleted_at: null,
          users: [],
          workers: [],
          companies: [],
          contacts: [],
          notification: null,
          category: 'other',
          source,
          url: evt.url || null,
        } as any
      })
    },
    mergeAndSortEvents(
      dispojob: CalendarEvent[],
      external: CalendarEvent[],
      sortBy: string,
      sortDesc: boolean
    ): CalendarEvent[] {
      const merged = [...dispojob, ...external]
      return merged.sort((a, b) => {
        const aVal = (a as any)[sortBy] ?? ''
        const bVal = (b as any)[sortBy] ?? ''
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true })
        return sortDesc ? -cmp : cmp
      })
    },
    async fetchUsers() {
      try {
        const params: any = { per_page: 0 }
        if (this.filters.space_ids && this.filters.space_ids.length > 0) {
          params.spaces = this.filters.space_ids.join(',')
        }
        const response = await axios.get('/v1/space/users', { params })
        const usersData = Array.isArray(response.data) ? response.data : (response.data.data || [])

        const currentUserId = getUserId()
        const numId = currentUserId ? Number(currentUserId) : null
        const meLabel = this.$vuetify.lang.t('$vuetify.me')

        const lastName = (u: any) => String(u.lastname ?? u.last_name ?? '').trim()
        const firstName = (u: any) => String(u.firstname ?? u.first_name ?? '').trim()
        const sortByLastFirst = (a: any, b: any) => {
          const cmpLast = lastName(a).localeCompare(lastName(b), undefined, { sensitivity: 'base' })
          if (cmpLast !== 0) return cmpLast
          return firstName(a).localeCompare(firstName(b), undefined, { sensitivity: 'base' })
        }

        const newUsers = usersData.map((user: any) => {
          const ln = lastName(user)
          const fn = firstName(user)
          const fullName = [ln, fn].filter(Boolean).join(' ')
          const isCurrentUser = !!(numId && Number(user.id) === numId)
          const displayName = getUserDisplayName(isCurrentUser, fullName, meLabel)
          return {
            ...user,
            lastname: ln,
            firstname: fn,
            full_name: displayName,
          }
        })

        // Preserve selected users that may not be in the new space's list
        const selectedIds = this.filters.user_ids || []
        const newUserIds = new Set(newUsers.map((u: any) => u.id))
        const missingSelectedUsers = selectedIds
          .filter((id: number) => !newUserIds.has(id))
          .map((id: number) => this.users.find((u: any) => u.id === id))
          .filter(Boolean)

        // Always ensure current user is in the list (so they can reselect after clearing)
        if (numId && !newUsers.some((u: any) => Number(u.id) === numId) &&
            !missingSelectedUsers.some((u: any) => Number(u.id) === numId)) {
          missingSelectedUsers.push({
            id: numId,
            firstname: meLabel,
            lastname: '',
            first_name: meLabel,
            last_name: '',
            full_name: meLabel,
          } as any)
        }

        // Set default: current user on first load when no filter is set
        let shouldRefreshEvents = false
        if (numId && !this.defaultUserFilterSet && selectedIds.length === 0) {
          this.filters.user_ids = [numId]
          this.defaultUserFilterSet = true
          shouldRefreshEvents = true
        }

        const allUsers = [...newUsers, ...missingSelectedUsers]
        const others = allUsers.filter((u: any) => Number(u.id) !== numId).sort(sortByLastFirst)
        this.users = numId
          ? [...allUsers.filter((u: any) => Number(u.id) === numId), ...others]
          : allUsers.sort(sortByLastFirst)

        if (shouldRefreshEvents) {
          this.$nextTick(() => this.refreshEvents())
        }
      } catch (error) {
        console.error('Failed to fetch users:', error)
      }
    },
    async fetchAgencies() {
      try {
        const response = await axios.get('/v1/spaces', { params: { deleted_at: false, per_page: 0 } })
        const spacesData = Array.isArray(response.data) ? response.data : (response.data.data || [])

        this.spaces = spacesData.map((space: any) => ({
          id: space.id,
          name: space.name,
        }))
      } catch (error) {
        console.error('Failed to fetch spaces:', error)
        this.spaces = []
      }
    },
    async fetchGoogleCalendarStatus() {
      try {
        const response = await axios.get('/v1/calendar/google/status')
        this.googleCalendarConnected = response.data?.connected === true
      } catch {
        this.googleCalendarConnected = false
      }
    },
    async fetchMicrosoftCalendarStatus() {
      try {
        const response = await axios.get('/v1/calendar/microsoft/status')
        this.microsoftCalendarConnected = response.data?.connected === true
      } catch {
        this.microsoftCalendarConnected = false
      }
    },
    shouldIncludeExternalCalendars(): boolean {
      const categories = this.filters.categories || []
      return categories.length === 0 || categories.includes('other')
    },
    shouldShowGoogleCalendar(): boolean {
      const currentUserId = getUserId()
      const userIds = this.filters.user_ids || []
      return this.googleCalendarConnected &&
        userIds.includes(Number(currentUserId)) &&
        this.shouldIncludeExternalCalendars()
    },
    shouldShowMicrosoftCalendar(): boolean {
      const currentUserId = getUserId()
      const userIds = this.filters.user_ids || []
      return this.microsoftCalendarConnected &&
        userIds.includes(Number(currentUserId)) &&
        this.shouldIncludeExternalCalendars()
    },
    addGoogleEventSource(calendarApi: any) {
      if (!calendarApi) {
        calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      }
      if (calendarApi && this.shouldShowGoogleCalendar()) {
        const existing = calendarApi.getEventSourceById('google-calendar')
        if (existing) return
        calendarApi.addEventSource({
          id: 'google-calendar',
          events: this.fetchGoogleCalendarEvents.bind(this),
        })
      }
    },
    removeGoogleEventSource() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        const source = calendarApi.getEventSourceById('google-calendar')
        if (source) source.remove()
      }
    },
    addMicrosoftEventSource(calendarApi: any) {
      if (!calendarApi) {
        calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      }
      if (calendarApi && this.shouldShowMicrosoftCalendar()) {
        const existing = calendarApi.getEventSourceById('microsoft-calendar')
        if (existing) return
        calendarApi.addEventSource({
          id: 'microsoft-calendar',
          events: this.fetchMicrosoftCalendarEvents.bind(this),
        })
      }
    },
    removeMicrosoftEventSource() {
      const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
      if (calendarApi) {
        const source = calendarApi.getEventSourceById('microsoft-calendar')
        if (source) source.remove()
      }
    },
    fetchGoogleCalendarEvents(
      fetchInfo: any,
      successCallback: (events: any[]) => void,
      failureCallback: (error: any) => void
    ): void {
      const from = fetchInfo.startStr.split('T')[0]
      const to = fetchInfo.endStr.split('T')[0]
      const colors = getCategoryColors()
      axios.get('/v1/calendar/google/events', { params: { from, to } })
        .then((response) => {
          const data = response.data
          const events = Array.isArray(data) ? data : (data?.events ?? [])
          if (data?.tokenInvalidated) {
            this.googleCalendarConnected = false
            this.removeGoogleEventSource()
          }
          const calendarEvents = events.map((event: any) => ({
            ...event,
            backgroundColor: colors.other,
            borderColor: colors.other,
            extendedProps: {
              ...event.extendedProps,
              category: 'other' as EventCategory,
            },
          }))
          successCallback(calendarEvents)
        })
        .catch((error) => {
          EventBus.$emit('snackbar', { axiosError: error })
          failureCallback(error)
        })
    },
    fetchMicrosoftCalendarEvents(
      fetchInfo: any,
      successCallback: (events: any[]) => void,
      failureCallback: (error: any) => void
    ): void {
      const from = fetchInfo.startStr.split('T')[0]
      const to = fetchInfo.endStr.split('T')[0]
      const colors = getCategoryColors()
      axios.get('/v1/calendar/microsoft/events', { params: { from, to } })
        .then((response) => {
          const data = response.data
          const events = Array.isArray(data) ? data : (data?.events ?? [])
          if (data?.tokenInvalidated) {
            this.microsoftCalendarConnected = false
            this.removeMicrosoftEventSource()
          }
          const calendarEvents = events.map((event: any) => ({
            ...event,
            backgroundColor: colors.other,
            borderColor: colors.other,
            extendedProps: {
              ...event.extendedProps,
              category: 'other' as EventCategory,
            },
          }))
          successCallback(calendarEvents)
        })
        .catch((error) => {
          EventBus.$emit('snackbar', { axiosError: error })
          failureCallback(error)
        })
    },
    toggleSelectAllUsers() {
      if (this.allUsersSelected) {
        this.deselectAllUsers()
      } else {
        this.selectAllUsers()
      }
    },
    selectAllUsers() {
      this.filters.user_ids = this.users.map(u => u.id)
      this.refreshEvents()
    },
    deselectAllUsers() {
      this.filters.user_ids = []
      this.refreshEvents()
    },
    removeUserFromFilter(userId: number) {
      this.filters.user_ids = (this.filters.user_ids || []).filter((id: number) => id !== userId)
      this.refreshEvents()
    },
    deselectAllSpaces() {
      this.filters.space_ids = []
      this.refreshEvents()
    },
    removeSpaceFromFilter(spaceId: number) {
      this.filters.space_ids = (this.filters.space_ids || []).filter((id: number) => id !== spaceId)
      this.refreshEvents()
    },
    removeCategoryFromFilter(category: EventCategory) {
      this.filters.categories = (this.filters.categories || []).filter((c: EventCategory) => c !== category)
      this.refreshEvents()
    },
    refreshEvents() {
      // Reload users when space filter changes
      this.fetchUsers()

      if (this.currentView === 'calendar') {
        const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
        if (calendarApi) {
          if (this.shouldShowGoogleCalendar()) {
            this.addGoogleEventSource(calendarApi)
          } else {
            this.removeGoogleEventSource()
          }
          if (this.shouldShowMicrosoftCalendar()) {
            this.addMicrosoftEventSource(calendarApi)
          } else {
            this.removeMicrosoftEventSource()
          }
          calendarApi.refetchEvents()
        }
      } else {
        this.fetchEvents()
      }
    },
    updateEventColors() {
      // Update colors of already loaded events without refetching from API
      if (this.currentView === 'calendar') {
        const calendarApi = (this.$refs.fullCalendar as InstanceType<typeof FullCalendar>)?.getApi() as any
        if (calendarApi) {
          const events = calendarApi.getEvents()
          events.forEach((event: any) => {
            const calendarEvent = event.extendedProps as CalendarEvent
            if (calendarEvent) {
              const newColor = getEventColor(calendarEvent)
              event.setProp('backgroundColor', newColor)
              event.setProp('borderColor', newColor)
            }
          })
          calendarApi.render()
        }
      } else {
        // For list view, Vue reactivity will handle it automatically via getEventColor()
        // Force re-render by updating a reactive property
        this.$forceUpdate()
      }
    },
    onCategoryColorChanged() {
      // Update colors of visible events without refetching from API
      this.updateEventColors()
      // Force category filter to recompute with new colors (categoryItems depends on getCategoryColors from localStorage)
      this.categoryColorsVersion++
    },
    removeFilter(key: string) {
      if (key.startsWith('space_id_')) {
        const sid = Number(key.replace('space_id_', ''))
        this.filters.space_ids = this.filters.space_ids?.filter(id => id !== sid) || []
      } else if (key.startsWith('user_id_')) {
        const uid = Number(key.replace('user_id_', ''))
        this.filters.user_ids = this.filters.user_ids?.filter(id => id !== uid) || []
      } else if (key === 'from') {
        this.filters.from = undefined
      } else if (key === 'to') {
        this.filters.to = undefined
      } else if (key.startsWith('category_')) {
        const cat = key.replace('category_', '') as EventCategory
        this.filters.categories = this.filters.categories?.filter(c => c !== cat)
      }
      this.refreshEvents()
    },
    openCreateDialog() {
      this.selectedEvent = null
      this.eventDialog = true
    },
    getEventCreatorName(event: CalendarEvent): string {
      if (event.user_first_name || event.user_last_name) {
        return [event.user_first_name, event.user_last_name].filter(Boolean).join(' ')
      }
      if (event.users && event.user_id) {
        const creator = event.users.find((u: { id: number }) => u.id === event.user_id)
        return creator ? [creator.first_name, creator.last_name].filter(Boolean).join(' ') : ''
      }
      return ''
    },
    isEventActionResponsible(item: { user_id?: number | null; users?: Array<{ id: number }> }): boolean {
      const currentUserId = getUserId()
      if (!currentUserId || currentUserId === 0) return false
      const uid = Number(currentUserId)
      if (item.user_id && Number(item.user_id) === uid) return true
      if (item.users && Array.isArray(item.users)) {
        return item.users.some((u: { id: number }) => Number(u.id) === uid)
      }
      return false
    },
    openEventDialog(event: CalendarEvent) {
      if ((event.source === 'google' || event.source === 'microsoft') && event.url) {
        globalThis.open(event.url, '_blank')
        return
      }
      this.selectedEvent = event
      this.eventDialog = true
    },
    confirmDelete(event: CalendarEvent) {
      this.selectedEvent = event
      this.deleteDialog = true
    },
    async deleteEvent() {
      if (!this.selectedEvent) return
      try {
        this.deleting = true
        await axios.delete(`/v1/calendar/events/${this.selectedEvent.id}`)
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.calendar_event_deleted') })
        this.deleteDialog = false
        this.refreshEvents()
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error })
      } finally {
        this.deleting = false
      }
    },
    onEventSaved() {
      this.eventDialog = false
      this.refreshEvents()
    },
    onEventDeleted() {
      this.eventDialog = false
      this.refreshEvents()
    },
    parseCompanyIds(companyIds: QueryParamValue): number[] {
      return this.parseIds(companyIds)
    },
    parseIds(ids: QueryParamValue): number[] {
      if (ids === undefined || ids === null || ids === '') return []
      if (Array.isArray(ids)) {
        const merged: number[] = []
        for (const part of ids) {
          merged.push(...this.parseIds(part as QueryParamValue))
        }
        return [...new Set(merged)].filter((id) => id > 0)
      }
      if (typeof ids === 'number' && Number.isFinite(ids) && ids > 0) {
        return [ids]
      }
      const str = String(ids).trim()
      if (!str) return []
      return str
        .split(',')
        .map((id) => Number.parseInt(id.trim(), 10))
        .filter((id) => !Number.isNaN(id) && id > 0)
    },
    parseQueryString(val: QueryParamValue): string {
      if (!val) return ''
      const str = Array.isArray(val) ? val[0] : val
      return (str && typeof str === 'string' ? decodeURIComponent(str) : '') || ''
    },
    buildPreselectedFromQuery() {
      const q = this.$route.query
      return {
        type: this.parseQueryString(q.type) || '',
        title: this.parseQueryString(q.title) || '',
        companyIds: this.parseIds(q.company_ids),
        workerIds: this.parseIds(q.worker_ids),
        userIds: this.parseIds(q.user_ids),
        startDate: this.parseQueryString(q.start_date) || '',
        startTime: this.parseQueryString(q.start_time) || '',
        endDate: this.parseQueryString(q.end_date) || '',
        endTime: this.parseQueryString(q.end_time) || '',
        description: this.parseQueryString(q.description) || '',
        address: this.parseQueryString(q.address) || '',
        result: this.parseQueryString(q.result) || '',
        isPrivate: this.parseQueryString(q.is_private) === '1' || this.parseQueryString(q.is_private) === 'true',
      }
    },
    addMinutes(dateStr: string, minutes: number): string {
      const date = new Date(dateStr)
      date.setMinutes(date.getMinutes() + minutes)
      return date.toISOString()
    },
    formatDateTime(dateStr: string): string {
      if (!dateStr) return ''
      const date = new Date(dateStr.replace(' ', 'T'))
      return date.toLocaleString(this.$vuetify.lang.current, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
})
